import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import * as Blockly from 'blockly';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { VariableActionRequest } from './field-variable-actions';

@Component({
  selector: 'app-blockly-variable-dialog',
  imports: [FormsModule, DialogModule, ButtonModule, InputTextModule],
  templateUrl: './blockly-variable-dialog.component.html',
})
export class BlocklyVariableDialogComponent {
  @ViewChild('nameInput') private nameInput?: ElementRef<HTMLInputElement>;
  protected request: VariableActionRequest | null = null;
  protected name = '';
  protected originalName = '';
  protected error = '';
  protected readonly renameTitle = $localize`:@@blockly-variable-rename-title:Variable umbenennen`;
  protected readonly deleteTitle = $localize`:@@blockly-variable-delete:Variable löschen`;
  protected readonly cancelLabel = $localize`:@@cancel:Abbrechen`;
  protected readonly renameLabel = $localize`:@@blockly-variable-rename-save:Speichern`;
  protected readonly deleteLabel = $localize`:@@delete:Löschen`;
  protected readonly nameLabel = $localize`:@@blockly-variable-name:Variablenname`;
  protected readonly renameHint = $localize`:@@blockly-variable-rename-hint:Der neue Name wird in allen Blöcken übernommen, die diese Variable verwenden.`;
  protected readonly deleteWarning = $localize`:@@blockly-variable-delete-warning:Alle Blöcke, die diese Variable verwenden, werden ebenfalls entfernt.`;

  public open(request: VariableActionRequest): void {
    const variable = request.workspace.getVariableMap().getVariableById(request.variableId);
    if (!variable) return;
    this.request = request;
    this.name = this.originalName = variable.getName();
    this.error = '';
  }

  protected get deleteQuestion(): string {
    return $localize`:@@blockly-variable-delete-question:Möchtest du die Variable „${this.originalName}:VARIABLE_NAME:“ wirklich löschen?`;
  }

  protected focusInput(): void {
    this.nameInput?.nativeElement.focus();
    this.nameInput?.nativeElement.select();
  }

  protected close(): void {
    this.request = null;
    this.error = '';
  }

  protected submit(): void {
    const request = this.request;
    if (!request) return;
    const map = request.workspace.getVariableMap();
    const variable = map.getVariableById(request.variableId);
    if (!variable) { this.close(); return; }

    if (request.mode === 'rename') {
      const name = this.name.replace(/[\s\u00a0]+/g, ' ').trim();
      if (!name) {
        this.error = $localize`:@@blockly-variable-name-required:Bitte gib einen Variablennamen ein.`;
        return;
      }
      const existing = Blockly.Variables.nameUsedWithAnyType(name, request.workspace);
      if ((existing && existing.getId() !== variable.getId()) ||
        Blockly.Variables.nameUsedWithConflictingParam(variable.getName(), name, request.workspace)) {
        this.error = $localize`:@@blockly-variable-name-conflict:Dieser Name wird bereits verwendet. Bitte wähle einen anderen Namen.`;
        return;
      }
      map.renameVariable(variable, name);
    } else {
      // Preserve Blockly's protection for legacy procedure parameters before
      // deleting through the variable map (which also groups the undo events).
      const uses = Blockly.Variables.getVariableUsesById(request.workspace, variable.getId());
      if (uses.some(block => block.type === 'procedures_defnoreturn' || block.type === 'procedures_defreturn')) {
        this.error = $localize`:@@blockly-variable-delete-procedure:Diese Variable wird als Funktionsparameter verwendet und kann hier nicht gelöscht werden.`;
        return;
      }
      map.deleteVariable(variable);
    }
    this.close();
  }
}
